export const ApiEndpoint = {
  ControlPlaneAuthByEmailDomain: 'ControlPlaneAuthByEmailDomain',
  ControlPlaneTenantByAlias: 'ControlPlaneTenantByAlias',
  EventBehavior: 'EventBehavior',
  Grains: 'Grains',
  GrainValues: 'GrainValues',
  GrainDictionary: 'GrainDictionary',
  ItemLifecycle: 'ItemLifecycle',
  CatalogProducts: 'CatalogProducts',
  CatalogCustomers: 'CatalogCustomers',
  CatalogCustomerDetails: 'CatalogCustomerDetails',
  PrimaryKeys: 'PrimaryKeys',
  SolutionsForecastList: 'SolutionsForecastList',
  SolutionsFetchRun: 'SolutionsFetchRun',
  ModelOutputsForecast: 'ModelOutputsForecast',
  ModelOutputsEvaluation: 'ModelOutputsEvaluation',
  ModelOutputsEvaluationList: 'ModelOutputsEvaluationList',
  SimulationReplenishment: 'SimulationReplenishment',
  SimulationReplenishmentSummary: 'SimulationReplenishmentSummary',
  SimulationFcfEntitlement: 'SimulationFcfEntitlement',
  SimulationFcfContributors: 'SimulationFcfContributors',
  SimulationInventoryCoverage: 'SimulationInventoryCoverage',
  SimulationHistoricalPerformance: 'SimulationHistoricalPerformance',
  ScenariosList: 'ScenariosList',
  ScenarioCreate: 'ScenarioCreate',
  ScenarioGet: 'ScenarioGet',
  ScenarioExecutionsList: 'ScenarioExecutionsList',
  ScenarioExecutionCreate: 'ScenarioExecutionCreate',
  ScenarioExecutionGet: 'ScenarioExecutionGet',
} as const;

export type ApiEndpoint = (typeof ApiEndpoint)[keyof typeof ApiEndpoint];

export interface EndpointConfig {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  tenantHeader?: string;
  description?: string;
  defaultHeaders?: Record<string, string>;
  cacheKey?: string; // optional key for react-query
}

export const ApiRegistry: Record<ApiEndpoint, EndpointConfig> = {
  [ApiEndpoint.ControlPlaneAuthByEmailDomain]: {
    method: 'GET',
    path: 'control-plane/authentication/email-domain/:emailDomain',
  },
  [ApiEndpoint.ControlPlaneTenantByAlias]: {
    method: 'GET',
    path: 'control-plane/tenants/:tenantAlias',
    tenantHeader: 'x-tenant-alias',
  },
  [ApiEndpoint.EventBehavior]: {
    method: 'POST',
    path: 'event/behavior',
  },
  [ApiEndpoint.Grains]: {
    method: 'GET',
    path: 'hierarchy/grain',
    description: 'Retrieve forecast grains for a Tenant',
  },
  [ApiEndpoint.GrainDictionary]: {
    method: 'GET',
    path: 'hierarchy/grain/dictionary',
  },
  [ApiEndpoint.GrainValues]: {
    method: 'POST',
    path: 'hierarchy/grain/values',
  },
  [ApiEndpoint.ItemLifecycle]: {
    method: 'POST',
    path: 'event/item-lifecycle',
  },
  [ApiEndpoint.PrimaryKeys]: {
    method: 'POST',
    path: 'event/primary-keys',
  },
  [ApiEndpoint.CatalogProducts]: {
    method: 'GET',
    path: 'catalog/products',
  },
  [ApiEndpoint.CatalogCustomers]: {
    method: 'GET',
    path: 'catalog/customers',
  },
  [ApiEndpoint.CatalogCustomerDetails]: {
    method: 'GET',
    path: 'catalog/customers/:customer_id',
  },
  [ApiEndpoint.SolutionsForecastList]: {
    method: 'GET',
    path: 'solutions/forecast',
  },
  [ApiEndpoint.SolutionsFetchRun]: {
    method: 'GET',
    path: 'execute/:solution_id',
  },
  [ApiEndpoint.ModelOutputsForecast]: {
    method: 'POST',
    path: 'model-outputs/forecast',
  },
  [ApiEndpoint.ModelOutputsEvaluation]: {
    method: 'POST',
    path: 'model-outputs/evaluation',
  },
  [ApiEndpoint.ModelOutputsEvaluationList]: {
    method: 'POST',
    path: 'model-outputs/evaluation/list',
  },
  [ApiEndpoint.SimulationReplenishment]: {
    method: 'GET',
    path: 'simulation-query/simulation/scenarios/:scenarioRunId/runs/:runId/replenishment',
  },
  [ApiEndpoint.SimulationReplenishmentSummary]: {
    method: 'GET',
    path: 'simulation-query/simulation/scenarios/:scenarioRunId/runs/:runId/replenishment/summary',
  },
  [ApiEndpoint.SimulationFcfEntitlement]: {
    method: 'GET',
    path: 'simulation-query/simulation/scenarios/:scenarioRunId/runs/:runId/fcf-entitlement',
  },
  [ApiEndpoint.SimulationFcfContributors]: {
    method: 'GET',
    path: 'simulation-query/simulation/scenarios/:scenarioRunId/runs/:runId/fcf-entitlement/contributors',
  },
  [ApiEndpoint.SimulationInventoryCoverage]: {
    method: 'GET',
    path: 'simulation-query/simulation/scenarios/:scenarioRunId/runs/:runId/skus/:sku/sites/:site/inventory-coverage',
  },
  [ApiEndpoint.SimulationHistoricalPerformance]: {
    method: 'GET',
    path: 'simulation-query/simulation/scenarios/:scenarioRunId/runs/:runId/skus/:sku/sites/:site/historical-performance',
  },

  // ── Simulation Metadata (scenario catalog) ──────────────────────────────────
  // Which backtests exist, their runs, and the config each run used. A different
  // service from simulation-query/ above, which serves results out of ClickHouse.
  [ApiEndpoint.ScenariosList]: {
    method: 'GET',
    path: 'simulation-metadata/scenarios',
    description: 'Backtest list. Paginated (limit/offset, default 20); excludes archived.',
  },
  [ApiEndpoint.ScenarioCreate]: {
    method: 'POST',
    path: 'simulation-metadata/scenarios',
    description: 'Create a scenario. scenario_run_id must match [A-Za-z0-9._-]+.',
  },
  [ApiEndpoint.ScenarioGet]: {
    method: 'GET',
    path: 'simulation-metadata/scenarios/:scenarioRunId',
    description: 'One scenario with its executions and experiment labels inline.',
  },
  [ApiEndpoint.ScenarioExecutionsList]: {
    method: 'GET',
    path: 'simulation-metadata/scenarios/:scenarioRunId/executions',
    description: 'Runs for one scenario, newest first. Paginated.',
  },
  [ApiEndpoint.ScenarioExecutionCreate]: {
    method: 'POST',
    path: 'simulation-metadata/scenarios/:scenarioRunId/executions',
    description:
      'Submit a run to Execution Service and record it. Send an Idempotency-Key header to make a retry safe: a repeat with the same key returns the original run instead of starting a second simulation.',
  },
  [ApiEndpoint.ScenarioExecutionGet]: {
    method: 'GET',
    path: 'simulation-metadata/scenarios/:scenarioRunId/executions/:runId',
    description: 'One run, including config_body. The config snapshot is returned here only, never in list responses.',
  },
} as const;
